import React, { useEffect, useRef, useState } from 'react';
import {
  supabase,
  uploadToStorage,
  createProject,
  consumeCreditsForProject,
  consumeCreditsForMotionTier,
  consumeCreditsForImagemotionUsage,
  quoteImagemotionGeneration,
  invokeGenerateMotion,
  failProject,
  fetchSavedLogos,
  uploadAndSaveLogo,
  deleteSavedLogo,
  setDefaultLogo,
  fetchBrandSettings,
  fetchImagemotionActivationState,
  type SavedLogo,
} from '../services/supabase';
import { LibraryTrackList, downloadRoyaltyFreeTrack, fetchRoyaltyFreeTracks, type LibraryTrack } from '@rehub/branded-viewer';
import { fetchPublicMotionTiers } from '../services/motion-tiers';
import type { PublicMotionTier } from '../types/motion-tiers';
import {
  PACKAGES,
  ALL_PACKAGES,
  STANDARD_TRANSITIONS,
  PREMIUM_TRANSITIONS,
  FILTERS,
  LOGO_POSITIONS,
  LOGO_BUMPER_DURATIONS,
  LOGO_BUMPER_ANIMATIONS,
  MUSIC_EFFECTS,
  RESOLUTIONS,
  ASPECT_RATIOS,
  DEFAULT_SETTINGS,
  TITLE_FONT_SIZES,
  recommendImagemotionPackage,
  resolveImagemotionPackage,
  imagemotionHasStandardFeatures,
  imagemotionHasPremiumFeatures,
  imagemotionIsTester,
  type ImagemotionQuality,
  type PackageConfig,
  type ProjectSettings,
} from '../types';
import {
  IMAGEMOTION_CLIP_EXPLAINER,
  IMAGEMOTION_DEFAULT_CLIP_SECONDS,
  WELCOME_CREDITS,
  quoteImagemotionUsage,
} from '@rehub/pricing';
import { detectAspectRatioFromSizes } from '../lib/aspect-ratio';
import ImageUploader, { type UploadedImage } from './ImageUploader';
import TitleCardPreview from './TitleCardPreview';
import DoubleCreditOffer from './DoubleCreditOffer';
import { trackImagemotionEvent, creditBalanceRange } from '../services/analytics';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  Music,
  Image as ImageIcon,
  Settings,
  Star,
  Coins,
  Zap,
  Lock,
  Type,
  Monitor,
  Palette,
  SlidersHorizontal,
  X,
  Film,
  AlertCircle,
  Play,
  Gift,
} from 'lucide-react';

type AppView = 'LOADING' | 'LOGIN' | 'DASHBOARD' | 'NEW_PROJECT' | 'HYPER_DUSK_PROJECT' | 'PROJECT_DETAIL';

const STEP_LABELS = ['Choose & Upload', 'Customise Video', 'Review & Create'] as const;
const DEMO_VIDEO_ID = 'lZxP4IsBzvE';
/** 1920×1080 frame. YouTube's own preview for this clip is only 480px and looks soft. */
const DEMO_VIDEO_POSTER = '/demo-london.jpg';
const MORE_EXAMPLES_URL = 'https://www.imagemotion.ai/examples';
const TESTER_LOGO_URL = 'https://imagemotion.rehub.studio/Logo.webp';
const PAID_PACKAGE_KEYS = ['quick', 'standard', 'premium'] as const;

/** Map customer motion-tier keys onto legacy package feature gates. */
function qualityFromMotionTierKey(tierKey: string): ImagemotionQuality {
  if (tierKey === 'ultra_motion') return 'premium';
  if (tierKey === 'premium_motion') return 'premium';
  if (tierKey === 'standard_motion') return 'standard';
  return 'standard';
}

const MOTION_TIER_CARD_STYLES: Record<string, { border: string; bg: string; ring: string; accent: string }> = {
  standard_motion: { border: 'border-blue-500', bg: 'bg-blue-900/20', ring: 'ring-blue-500/30', accent: 'text-blue-400' },
  premium_motion: { border: 'border-amber-500', bg: 'bg-amber-900/20', ring: 'ring-amber-500/30', accent: 'text-amber-400' },
  ultra_motion: { border: 'border-violet-500', bg: 'bg-violet-900/20', ring: 'ring-violet-500/30', accent: 'text-violet-300' },
};

interface NewProjectProps {
  userId: string;
  userCredits: number;
  onNavigate: (view: AppView, projectId?: string) => void;
  onCreditsUpdated: (newCredits: number) => void;
  onOpenPlans: () => void;
  onOpenBuyCredits: () => void;
  testerEligible?: boolean;
  doubleCreditEligible?: boolean;
  isNewUser?: boolean;
  onActivationRefresh?: () => void;
}

const TIER_STYLES: Record<
  ImagemotionQuality,
  { border: string; bg: string; ring: string; accent: string }
> = {
  tester: { border: 'border-emerald-500', bg: 'bg-emerald-900/20', ring: 'ring-emerald-500/30', accent: 'text-emerald-400' },
  quick: { border: 'border-slate-400', bg: 'bg-slate-800/60', ring: 'ring-slate-400/30', accent: 'text-slate-300' },
  standard: { border: 'border-blue-500', bg: 'bg-blue-900/20', ring: 'ring-blue-500/30', accent: 'text-blue-400' },
  premium: { border: 'border-amber-500', bg: 'bg-amber-900/20', ring: 'ring-amber-500/30', accent: 'text-amber-400' },
  ultra_premium: { border: 'border-purple-500', bg: 'bg-purple-900/20', ring: 'ring-purple-500/30', accent: 'text-purple-400' },
};

const SELECTED_MARK: Record<ImagemotionQuality, string> = {
  tester: 'bg-emerald-400 text-emerald-950',
  quick: 'bg-slate-200 text-slate-900',
  standard: 'bg-blue-400 text-blue-950',
  premium: 'bg-amber-400 text-amber-950',
  ultra_premium: 'bg-purple-400 text-purple-950',
};

function SelectedMark({ quality }: { quality: ImagemotionQuality }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${SELECTED_MARK[quality]}`}>
      <Check className="w-3 h-3" />
      Selected
    </span>
  );
}

/** Image limit is shown as its own line, so the bullet list does not repeat it. */
function detailFeatures(features: readonly string[]): string[] {
  return features.filter((feature) => !feature.startsWith('Animate up to '));
}

function formatFinishedDuration(seconds: number): string {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  if (total < 60) return `${total} ${total === 1 ? 'second' : 'seconds'}`;
  const minutes = Math.floor(total / 60);
  const remainder = total % 60;
  if (remainder === 0) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  return `${minutes} min ${remainder} sec`;
}

function SettingsSection({
  icon,
  title,
  locked,
  requiredTier,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  locked?: boolean;
  requiredTier?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`relative ${locked ? 'opacity-40 pointer-events-none select-none' : ''}`}>
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <label className="text-sm font-medium text-slate-300">{title}</label>
        {locked && requiredTier && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
            <Lock className="w-3 h-3" />
            {requiredTier}
          </span>
        )}
      </div>
      {children}
      {locked && requiredTier && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs text-amber-400 bg-black/70 px-3 py-1 rounded-full border border-amber-500/30">
            Select {requiredTier} or above to unlock
          </span>
        </div>
      )}
    </div>
  );
}

function YouTubeFacade({
  videoId,
  poster,
  onPlay,
}: {
  videoId: string;
  poster: string;
  onPlay: () => void;
}) {
  const [playing, setPlaying] = useState(false);

  if (playing) {
    return (
      <div className="relative w-full aspect-video rounded-xl overflow-hidden border border-slate-700 bg-black">
        <iframe
          title="ImageMotion demo"
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        setPlaying(true);
        onPlay();
      }}
      className="group relative w-full aspect-video rounded-xl overflow-hidden border border-slate-700 bg-slate-900 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      aria-label="Play ImageMotion demo video"
    >
      <img
        src={poster}
        alt="ImageMotion demo thumbnail"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-slate-950/40 group-hover:bg-slate-950/30 transition-colors" />
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-600 text-white shadow-lg group-hover:scale-105 transition-transform">
          <Play className="w-7 h-7 ml-0.5" fill="currentColor" />
        </span>
      </div>
    </button>
  );
}

function suggestNameFromPath(path: string | null | undefined): string | null {
  if (!path) return null;
  const base = path.split('/').pop() || '';
  // Storage paths are usually timestamp-random.ext — skip those.
  if (/^\d+-[a-z0-9]+\./i.test(base)) return null;
  const cleaned = base.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').trim();
  return cleaned.length >= 2 ? cleaned : null;
}

function creditErrorMessage(
  code: string,
  pkg: PackageConfig,
  requiredCredits?: number
): string {
  switch (code) {
    case 'TESTER_ALREADY_USED':
      return 'You have already used your Tester Video. Choose a paid package to continue.';
    case 'TESTER_IN_PROGRESS':
      return 'Your Tester Video is already being created. Check your projects for progress.';
    case 'INSUFFICIENT_CREDITS':
      return `Not enough credits. You need ${requiredCredits ?? pkg.credits} credits for ${pkg.label}.`;
    case 'IMAGE_LIMIT':
      return `Image count is outside the limit for ${pkg.label} (max ${pkg.maxImages}).`;
    case 'CREDIT_MISMATCH':
    case 'TIER_CREDIT_MISMATCH':
      return requiredCredits
        ? `This package now costs ${requiredCredits} credits. Review the total and try again.`
        : 'Credit amount mismatch. Refresh the page and try again.';
    default:
      return code || 'Failed to process credits. Please try again.';
  }
}

export default function NewProject({
  userId,
  userCredits,
  onNavigate,
  onCreditsUpdated,
  onOpenPlans: _onOpenPlans,
  onOpenBuyCredits,
  testerEligible: testerEligibleProp = false,
  doubleCreditEligible = false,
  isNewUser = false,
  onActivationRefresh,
}: NewProjectProps) {
  const [step, setStep] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [activationTesterEligible, setActivationTesterEligible] = useState(testerEligibleProp);
  const testerEligible = Boolean(testerEligibleProp || activationTesterEligible);

  const [quality, setQuality] = useState<ImagemotionQuality>(
    testerEligibleProp ? 'tester' : 'standard'
  );
  const [userOverridePackage, setUserOverridePackage] = useState(false);
  const [motionTiers, setMotionTiers] = useState<PublicMotionTier[]>([]);
  const [motionTiersLoaded, setMotionTiersLoaded] = useState(false);
  const [selectedMotionTierId, setSelectedMotionTierId] = useState<string | null>(null);
  const [projectName, setProjectName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [serverQuote, setServerQuote] = useState<Record<string, unknown> | null>(null);
  const [priceChanged, setPriceChanged] = useState<{ required: number } | null>(null);

  const [settings, setSettings] = useState<ProjectSettings>({ ...DEFAULT_SETTINGS });
  const [musicMode, setMusicMode] = useState<'none' | 'upload' | 'url' | 'library'>('none');
  const [libraryTracks, setLibraryTracks] = useState<LibraryTrack[]>([]);
  const [libraryAddingId, setLibraryAddingId] = useState<string | null>(null);
  const [libraryTitle, setLibraryTitle] = useState<string | null>(null);
  const [musicUrl, setMusicUrl] = useState('');
  const [musicUploading, setMusicUploading] = useState(false);
  const [uploadedMusicUrl, setUploadedMusicUrl] = useState<string | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [uploadedLogoUrl, setUploadedLogoUrl] = useState<string | null>(null);
  const [savedLogos, setSavedLogos] = useState<SavedLogo[]>([]);
  const [savedLogosLoaded, setSavedLogosLoaded] = useState(false);
  const [brandLogoUrl, setBrandLogoUrl] = useState<string | null>(null);

  const pageViewedRef = useRef(false);
  const testerViewedRef = useRef(false);
  const prevImageCountRef = useRef(0);

  const selectedMotionTier =
    motionTiers.find((t) => t.tier_id === selectedMotionTierId) ??
    (motionTiers.length === 1 ? motionTiers[0] : null);
  const useMotionTierPricing = motionTiersLoaded && motionTiers.length > 0 && !imagemotionIsTester(quality);

  const selectedPackage = resolveImagemotionPackage(quality);
  const isTester = imagemotionIsTester(quality);
  const isStandardPlus = imagemotionHasStandardFeatures(quality);
  const isPremiumPlus = imagemotionHasPremiumFeatures(quality);
  const isQuick = quality === 'quick';
  const dissolveOnly = isTester || isQuick;

  const maxImages = selectedPackage.maxImages;

  const transitions = isPremiumPlus
    ? PREMIUM_TRANSITIONS
    : dissolveOnly
      ? STANDARD_TRANSITIONS.filter((t) => t.value === 'dissolve')
      : STANDARD_TRANSITIONS;

  const allImagesUploaded = images.length >= 2 && images.every((img) => img.url && !img.uploading);
  const imagesWithinLimit = images.length <= maxImages;
  const canProceedStep1 = allImagesUploaded && imagesWithinLimit;
  const identicalMotionTiers =
    motionTiers.length > 1 &&
    motionTiers.every((t) => t.duration_seconds === motionTiers[0].duration_seconds);
  const quoteInput = {
    generated_clip_count: images.length,
    clip_seconds: IMAGEMOTION_DEFAULT_CLIP_SECONDS,
    kind: 'generate' as const,
    tester: isTester,
    intro_text: !isTester && isStandardPlus && Boolean(settings.intro_text),
    outro_text: !isTester && isStandardPlus && Boolean(settings.outro_text),
    logo_intro: !isTester && isStandardPlus && settings.logo_intro_card,
    logo_outro: !isTester && isStandardPlus && settings.logo_outro_card,
    logo_bumper_seconds: settings.logo_bumper_duration || 3,
  };
  const localQuote = quoteImagemotionUsage({
    generatedClipCount: images.length,
    clipSeconds: IMAGEMOTION_DEFAULT_CLIP_SECONDS,
    tester: isTester,
    introText: quoteInput.intro_text,
    outroText: quoteInput.outro_text,
    logoIntro: quoteInput.logo_intro,
    logoOutro: quoteInput.logo_outro,
    logoBumperSeconds: quoteInput.logo_bumper_seconds,
  });
  const usageCredits =
    serverQuote?.pricing_mode === 'usage' && typeof serverQuote.required_credits === 'number'
      ? Number(serverQuote.required_credits)
      : localQuote.chargeable
        ? localQuote.requiredCredits
        : null;
  const showDistinctMotionTiers =
    useMotionTierPricing && motionTiers.length > 1 && !identicalMotionTiers;
  const legacyCredits =
    showDistinctMotionTiers && selectedMotionTier
      ? selectedMotionTier.credit_cost
      : selectedPackage.credits;
  const creditsNeeded = usageCredits ?? legacyCredits;
  const creditShortfall = Math.max(0, creditsNeeded - userCredits);
  const canAfford = userCredits >= creditsNeeded;
  const userSegment = isNewUser ? 'new' : 'returning';
  const chargeLabel =
    useMotionTierPricing && selectedMotionTier
      ? selectedMotionTier.display_name
      : selectedPackage.label;

  const analyticsBase = {
    credit_balance_range: creditBalanceRange(userCredits),
    user_segment: userSegment as 'new' | 'returning',
    tester_eligible: testerEligible,
    offer_eligible: doubleCreditEligible,
  };

  useEffect(() => {
    if (localQuote.pricingMode === 'usage') return;
    console.info(
      'Usage pricing is prepared but not live yet — this charge uses the current package total until tax, card-fee and FX inputs are set.',
      { pricingMode: localQuote.pricingMode, packageId: quality, packageCredits: selectedPackage.credits }
    );
  }, [localQuote.pricingMode, quality, selectedPackage.credits]);

  // Fetch activation + page view analytics
  useEffect(() => {
    let cancelled = false;
    fetchImagemotionActivationState(userId).then((state) => {
      if (cancelled) return;
      setActivationTesterEligible(Boolean(state.tester_eligible));
      if (!userOverridePackage && state.tester_eligible && quality !== 'tester' && images.length === 0) {
        setQuality('tester');
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only activation fetch
  }, [userId]);

  // Load customer-safe motion quality tiers (no fal endpoints / costs).
  useEffect(() => {
    let cancelled = false;
    fetchPublicMotionTiers()
      .then((tiers) => {
        if (cancelled) return;
        setMotionTiers(tiers);
        setMotionTiersLoaded(true);
        if (tiers.length === 1) {
          setSelectedMotionTierId(tiers[0].tier_id);
        } else if (tiers.length > 1 && !selectedMotionTierId) {
          const recommended = tiers.find((t) => t.is_recommended) ?? tiers[0];
          setSelectedMotionTierId(recommended.tier_id);
        }
      })
      .catch((err) => {
        console.error('Failed to load motion tiers:', err);
        if (!cancelled) setMotionTiersLoaded(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only
  }, []);

  // When a motion tier is selected (and not on tester), sync legacy quality for feature gates.
  useEffect(() => {
    if (isTester || !selectedMotionTier) return;
    if (userOverridePackage && quality !== 'tester') return;
    const mapped = qualityFromMotionTierKey(selectedMotionTier.tier_key);
    if (quality !== mapped && quality !== 'tester' && quality !== 'quick') {
      setQuality(mapped);
    }
  }, [selectedMotionTier, isTester, userOverridePackage, quality]);

  useEffect(() => {
    if (pageViewedRef.current) return;
    pageViewedRef.current = true;
    trackImagemotionEvent('imagemotion_page_viewed', userId, analyticsBase);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once on mount
  }, [userId]);

  useEffect(() => {
    if (!testerEligible || testerViewedRef.current) return;
    testerViewedRef.current = true;
    trackImagemotionEvent('imagemotion_tester_viewed', userId, analyticsBase);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testerEligible, userId]);

  // Soft-recommend package from image count unless user overrode
  useEffect(() => {
    if (userOverridePackage) return;
    const recommended = recommendImagemotionPackage(images.length, testerEligible);
    setQuality(recommended);
  }, [images.length, testerEligible, userOverridePackage]);

  useEffect(() => {
    let cancelled = false;
    quoteImagemotionGeneration(quoteInput).then((res) => {
      if (cancelled || !res.success || !res.quote) return;
      setServerQuote(res.quote);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- quote when selections change
  }, [
    images.length,
    isTester,
    isStandardPlus,
    settings.intro_text,
    settings.outro_text,
    settings.logo_intro_card,
    settings.logo_outro_card,
    settings.logo_bumper_duration,
  ]);

  // Force dissolve for tester/quick
  useEffect(() => {
    if (dissolveOnly && settings.transition_effect !== 'dissolve') {
      setSettings((prev) => ({ ...prev, transition_effect: 'dissolve' }));
    }
  }, [dissolveOnly, settings.transition_effect]);

  // Suggest project name from first image path when practical
  useEffect(() => {
    if (nameTouched || projectName.trim()) return;
    const first = images.find((img) => img.path);
    const suggestion = suggestNameFromPath(first?.path);
    if (suggestion) setProjectName(suggestion);
  }, [images, nameTouched, projectName]);

  // Track image count changes
  useEffect(() => {
    if (images.length === prevImageCountRef.current) return;
    if (images.length > 0) {
      trackImagemotionEvent('imagemotion_images_uploaded', userId, {
        ...analyticsBase,
        image_count: images.length,
        package_id: quality,
      });
    }
    prevImageCountRef.current = images.length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images.length, userId]);

  useEffect(() => {
    if (!savedLogosLoaded) {
      Promise.all([fetchSavedLogos(userId), fetchBrandSettings(userId)]).then(([logos, brand]) => {
        setSavedLogos(logos);
        setSavedLogosLoaded(true);
        if (brand?.logo_url) setBrandLogoUrl(brand.logo_url);
        const defaultLogo = logos.find((l) => l.is_default);
        if (defaultLogo && !uploadedLogoUrl) {
          setUploadedLogoUrl(defaultLogo.logo_url);
          setLogoPreview(defaultLogo.logo_url);
        } else if (!uploadedLogoUrl && brand?.logo_url) {
          setUploadedLogoUrl(brand.logo_url);
          setLogoPreview(brand.logo_url);
        }
        if (brand) {
          setSettings((prev) => ({
            ...prev,
            logo_intro_card: brand.logo_intro_enabled ?? prev.logo_intro_card,
            logo_outro_card: brand.logo_outro_enabled ?? prev.logo_outro_card,
            logo_bumper_duration: brand.logo_bumper_duration ?? prev.logo_bumper_duration,
            logo_bumper_tagline: brand.logo_bumper_tagline ?? prev.logo_bumper_tagline,
          }));
        }
      });
    }
  }, [userId, savedLogosLoaded, uploadedLogoUrl]);

  const getUpgradeSuggestion = (): { message: string; targetTier: 'quick' | 'standard' | 'premium' } | null => {
    if (imagesWithinLimit || images.length === 0) return null;
    const count = images.length;
    if (count <= PACKAGES.quick.maxImages && quality !== 'quick') {
      return { message: `You uploaded ${count} images. Quick Video supports up to ${PACKAGES.quick.maxImages}.`, targetTier: 'quick' };
    }
    if (count <= PACKAGES.standard.maxImages && (quality === 'tester' || quality === 'quick')) {
      return {
        message: `You uploaded ${count} images. Upgrade to Standard to include them all.`,
        targetTier: 'standard',
      };
    }
    if (count <= PACKAGES.premium.maxImages) {
      return {
        message: `You uploaded ${count} images. Upgrade to Premium to include them all.`,
        targetTier: 'premium',
      };
    }
    return {
      message: `You uploaded ${count} images. Premium supports up to ${PACKAGES.premium.maxImages}. Remove some images to continue.`,
      targetTier: 'premium',
    };
  };

  const upgradeSuggestion = getUpgradeSuggestion();

  const selectPackage = (next: ImagemotionQuality) => {
    setUserOverridePackage(true);
    setQuality(next);
    if (imagemotionIsTester(next)) {
      trackImagemotionEvent('imagemotion_tester_selected', userId, {
        ...analyticsBase,
        package_id: next,
        image_count: images.length,
      });
    } else {
      trackImagemotionEvent('imagemotion_package_selected', userId, {
        ...analyticsBase,
        package_id: next,
        image_count: images.length,
      });
    }
  };

  const openBuyCredits = (source: string) => {
    trackImagemotionEvent('buy_credits_clicked', userId, {
      ...analyticsBase,
      package_id: quality,
      image_count: images.length,
      source,
    });
    onOpenBuyCredits();
  };

  const getLogoPositionStyle = (position: string | undefined): React.CSSProperties => {
    const pos = position || 'bottomRight';
    const style: React.CSSProperties = {};
    if (pos.includes('top')) style.top = '4%';
    else if (pos.includes('bottom')) style.bottom = '4%';
    else {
      style.top = '50%';
      style.transform = 'translateY(-50%)';
    }
    if (pos.includes('Left') || pos === 'left') style.left = '3%';
    else if (pos.includes('Right') || pos === 'right') style.right = '3%';
    else {
      style.left = '50%';
      style.transform = (style.transform || '') + ' translateX(-50%)';
    }
    return style;
  };

  const updateSetting = <K extends keyof ProjectSettings>(key: K, value: ProjectSettings[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const detectedAspectRatio = detectAspectRatioFromSizes(
    images.map((img) => ({ width: img.width, height: img.height })),
  );

  useEffect(() => {
    const next = detectedAspectRatio ?? '16:9';
    setSettings((prev) => (prev.aspect_ratio === next ? prev : { ...prev, aspect_ratio: next }));
  }, [detectedAspectRatio]);

  useEffect(() => {
    let cancelled = false;
    fetchRoyaltyFreeTracks(supabase).then((tracks) => {
      if (!cancelled) setLibraryTracks(tracks);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLibraryTrack = async (track: LibraryTrack) => {
    setLibraryAddingId(track.id);
    setMusicUploading(true);
    setMusicMode('library');
    try {
      const file = await downloadRoyaltyFreeTrack(supabase, track);
      const result = await uploadToStorage(file, 'music', userId);
      setUploadedMusicUrl(result.url);
      setLibraryTitle(track.title);
    } catch {
      setError('Failed to add that track');
    } finally {
      setLibraryAddingId(null);
      setMusicUploading(false);
    }
  };

  const handleMusicFileUpload = async (file: File) => {
    setMusicUploading(true);
    try {
      const result = await uploadToStorage(file, 'music', userId);
      setUploadedMusicUrl(result.url);
    } catch {
      setError('Failed to upload music file');
    }
    setMusicUploading(false);
  };

  const handleLogoUpload = async (file: File) => {
    setLogoPreview(URL.createObjectURL(file));
    setLogoUploading(true);
    try {
      const { url, savedLogo } = await uploadAndSaveLogo(file, userId);
      setUploadedLogoUrl(url);
      if (savedLogo) setSavedLogos((prev) => [savedLogo, ...prev]);
    } catch {
      setError('Failed to upload logo');
    }
    setLogoUploading(false);
  };

  const handleSelectSavedLogo = (logo: SavedLogo) => {
    setUploadedLogoUrl(logo.logo_url);
    setLogoPreview(logo.logo_url);
  };

  const handleDeleteSavedLogo = async (logoId: string) => {
    try {
      await deleteSavedLogo(logoId);
      setSavedLogos((prev) => prev.filter((l) => l.id !== logoId));
      const deleted = savedLogos.find((l) => l.id === logoId);
      if (deleted && uploadedLogoUrl === deleted.logo_url) {
        setUploadedLogoUrl(null);
        setLogoPreview(null);
      }
    } catch {
      /* ignore */
    }
  };

  const handleSetDefaultLogo = async (logoId: string) => {
    try {
      await setDefaultLogo(logoId);
      setSavedLogos((prev) => prev.map((l) => ({ ...l, is_default: l.id === logoId })));
    } catch {
      /* ignore */
    }
  };

  const handleSubmit = async () => {
    if (!canProceedStep1 || submitting) return;
    if (!canAfford) {
      openBuyCredits('sticky_create');
      return;
    }

    setSubmitting(true);
    setError(null);

    trackImagemotionEvent('imagemotion_creation_started', userId, {
      ...analyticsBase,
      package_id: quality,
      image_count: images.length,
    });

    try {
      const finalMusicUrl = isTester
        ? null
        : musicMode === 'upload' || musicMode === 'library'
          ? uploadedMusicUrl
          : musicMode === 'url'
            ? musicUrl
            : null;

      const finalLogoUrl = isTester ? TESTER_LOGO_URL : uploadedLogoUrl;

      const outputAspectRatio = detectedAspectRatio ?? settings.aspect_ratio ?? '16:9';
      const finalSettings: ProjectSettings = isTester
        ? {
            ...settings,
            aspect_ratio: outputAspectRatio,
            transition_effect: 'dissolve',
            clip_duration: IMAGEMOTION_DEFAULT_CLIP_SECONDS,
            filter: null,
            intro_text: null,
            outro_text: null,
            logo_intro_card: false,
            logo_outro_card: false,
            logo_on_title_cards: false,
            logo_bumper_tagline: null,
          }
        : {
            ...settings,
            aspect_ratio: outputAspectRatio,
            transition_effect: dissolveOnly ? 'dissolve' : settings.transition_effect || 'dissolve',
            intro_text: isStandardPlus ? settings.intro_text : null,
            outro_text: isStandardPlus ? settings.outro_text : null,
            logo_intro_card: isStandardPlus ? settings.logo_intro_card : false,
            logo_outro_card: isStandardPlus ? settings.logo_outro_card : false,
            logo_on_title_cards: isStandardPlus ? settings.logo_on_title_cards : false,
            filter: isPremiumPlus ? settings.filter : null,
          };

      const projectId = await createProject({
        userId,
        name: projectName.trim(),
        quality,
        motionTierId: useMotionTierPricing ? selectedMotionTier?.tier_id ?? null : null,
        motionTierVersionId: useMotionTierPricing ? selectedMotionTier?.version_id ?? null : null,
        musicUrl: finalMusicUrl,
        logoUrl: finalLogoUrl,
        transitionType: finalSettings.transition_effect || 'dissolve',
        settings: {
          ...finalSettings,
          clip_duration: IMAGEMOTION_DEFAULT_CLIP_SECONDS,
        },
        images: images.map((img, index) => ({
          url: img.url!,
          path: img.path ?? null,
          orderIndex: index,
        })),
      });

      let creditResult: {
        success: boolean;
        error?: string;
        credits_remaining?: number;
        required_credits?: number;
      } = await consumeCreditsForImagemotionUsage({
        userId,
        projectId,
        credits: creditsNeeded,
        imageCount: images.length,
        kind: 'generate',
        input: quoteInput,
        idempotencyKey: `generate:${projectId}`,
        legacyTierId: showDistinctMotionTiers ? selectedMotionTier?.tier_id ?? null : null,
      });

      if (creditResult.error === 'ABORTED') {
        await failProject(projectId, 'Credit charge was interrupted');
        setError('The request was interrupted. Please try creating the video again.');
        setSubmitting(false);
        return;
      }

      if (creditResult.error === 'USAGE_RPC_MISSING') {
        if (useMotionTierPricing && selectedMotionTier) {
          creditResult = await consumeCreditsForMotionTier({
            userId,
            projectId,
            tierId: selectedMotionTier.tier_id,
            credits: creditsNeeded,
            imageCount: images.length,
          });
        } else {
          creditResult = await consumeCreditsForProject(userId, projectId, selectedPackage.credits);
        }
      }

      if (
        (creditResult.error === 'PRICE_CHANGED' ||
          creditResult.error === 'CREDIT_MISMATCH' ||
          creditResult.error === 'TIER_CREDIT_MISMATCH') &&
        creditResult.required_credits
      ) {
        await failProject(
          projectId,
          `Credit total changed to ${creditResult.required_credits}`
        );
        setPriceChanged({ required: creditResult.required_credits });
        setError(
          `The credit total changed to ${creditResult.required_credits}. Review the new total before generating.`
        );
        setSubmitting(false);
        return;
      }

      if (!creditResult.success) {
        const code = creditResult.error || 'UNKNOWN';
        await failProject(projectId, creditErrorMessage(code, selectedPackage, creditResult.required_credits));
        setError(creditErrorMessage(code, selectedPackage, creditResult.required_credits));
        trackImagemotionEvent('imagemotion_creation_failed', userId, {
          ...analyticsBase,
          package_id: quality,
          image_count: images.length,
          source: code,
        });
        setSubmitting(false);
        return;
      }

      if (creditResult.credits_remaining !== undefined) {
        onCreditsUpdated(creditResult.credits_remaining);
      }

      // Tier-resolved generation — server looks up model; never send fal endpoints.
      if (useMotionTierPricing && selectedMotionTier) {
        const gen = await invokeGenerateMotion({
          projectId,
          tierId: selectedMotionTier.tier_id,
        });
        if (!gen.success) {
          console.warn('Motion submit deferred/failed:', gen.error);
          // Project stays queued/generating; ProjectDetail polling will retry.
        }
      }

      trackImagemotionEvent('imagemotion_creation_completed', userId, {
        ...analyticsBase,
        package_id: quality,
        image_count: images.length,
      });

      onActivationRefresh?.();
      onNavigate('PROJECT_DETAIL', projectId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create project';
      setError(message);
      trackImagemotionEvent('imagemotion_creation_failed', userId, {
        ...analyticsBase,
        package_id: quality,
        image_count: images.length,
        source: 'exception',
      });
      setSubmitting(false);
    }
  };

  const stickyCtaLabel = !canAfford
    ? 'Buy credits to create this video'
    : step < 3
      ? `Continue with ${chargeLabel}`
      : isTester
        ? `Create Tester Video — ${creditsNeeded} credits`
        : `Create ${chargeLabel} — ${creditsNeeded} credits`;

  const stickyDisabled =
    submitting ||
    (canAfford && step === 1 && !canProceedStep1) ||
    (canAfford && step === 3 && !canProceedStep1);

  const handleStickyPrimary = () => {
    if (!canAfford) {
      openBuyCredits('sticky_bar');
      return;
    }
    if (step < 3) {
      if (step === 1 && !canProceedStep1) return;
      setError(null);
      setStep(step + 1);
      return;
    }
    void handleSubmit();
  };

  const recommendedId = recommendImagemotionPackage(images.length, testerEligible);

  return (
    <div className="max-w-[1280px] mx-auto animate-fade-in pb-28">
      {/* Intro */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10 items-center">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
            Turn your property photos into a polished video
          </h1>
          <p className="mt-3 text-slate-400 text-sm sm:text-base leading-relaxed">
            Upload your listing photos and create a ready-to-share 1080p property video without
            editing it yourself.
          </p>
          <ul className="mt-5 space-y-2 text-sm text-slate-300">
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
              Professional 1080p export
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
              No video-editing experience needed
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
              Ready for property listings and social media
            </li>
          </ul>
          {testerEligible && (
            <p className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-emerald-300 bg-emerald-900/20 border border-emerald-500/30 rounded-lg px-3 py-2">
              <Gift className="w-4 h-4" />
              Create your first Tester Video with your {WELCOME_CREDITS} welcome credits
            </p>
          )}
        </div>
        <div>
          <YouTubeFacade
            videoId={DEMO_VIDEO_ID}
            poster={DEMO_VIDEO_POSTER}
            onPlay={() =>
              trackImagemotionEvent('imagemotion_demo_played', userId, {
                ...analyticsBase,
                source: 'new_project_intro',
              })
            }
          />
          <a
            href={MORE_EXAMPLES_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex min-h-11 items-center justify-center rounded-lg border border-slate-600 px-5 text-sm font-semibold text-white transition-colors hover:border-slate-400 hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            See more examples
          </a>
        </div>
      </section>

      {doubleCreditEligible && (
        <div className="mb-8">
          <DoubleCreditOffer
            userId={userId}
            currency="GBP"
            processingId={null}
            variant="inline"
            onSelect={() => {
              trackImagemotionEvent('buy_credits_clicked', userId, {
                ...analyticsBase,
                source: 'new_project_double_offer',
              });
              onOpenBuyCredits();
            }}
          />
        </div>
      )}

      {/* Step indicator */}
      <div className="flex items-center justify-center gap-2 mb-8">
        {STEP_LABELS.map((label, index) => {
          const stepNum = index + 1;
          const isActive = step === stepNum;
          const isComplete = step > stepNum;

          return (
            <div key={label} className="flex items-center">
              {index > 0 && (
                <div className={`w-8 sm:w-12 h-0.5 mx-1 ${isComplete ? 'bg-blue-600' : 'bg-slate-800'}`} />
              )}
              <div className="flex items-center gap-2">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    isComplete || isActive ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-500'
                  }`}
                >
                  {isComplete ? <Check className="w-4 h-4" /> : stepNum}
                </div>
                <span className={`text-sm hidden sm:inline ${isActive ? 'font-medium text-white' : 'text-slate-500'}`}>
                  {label}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-lg bg-red-900/20 border border-red-900/50 text-red-400 text-sm animate-shake">
          {error}
        </div>
      )}

      <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
        {/* ───────── Step 1: Upload Photos ───────── */}
        {step === 1 && (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Coins className="w-6 h-6 text-blue-500" />
              <h2 className="text-lg font-semibold text-white">
                {showDistinctMotionTiers ? 'Choose Motion Quality' : 'Choose Your Package'}
              </h2>
            </div>

            {/* Tester welcome card — visually separate from paid packages */}
            {testerEligible && (
              <button
                type="button"
                aria-pressed={quality === 'tester'}
                onClick={() => selectPackage('tester')}
                className={`w-full mb-5 p-5 rounded-xl border-2 text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 ${
                  quality === 'tester'
                    ? `${TIER_STYLES.tester.border} ${TIER_STYLES.tester.bg} ring-1 ${TIER_STYLES.tester.ring}`
                    : 'border-emerald-900/60 bg-slate-800/40 hover:border-emerald-600/50'
                }`}
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex flex-wrap items-center gap-2 min-w-0">
                    <Gift className="w-5 h-5 text-emerald-400" />
                    <h3 className="text-lg font-bold text-white">{ALL_PACKAGES.tester.label}</h3>
                    {recommendedId === 'tester' && (
                      <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-300 bg-emerald-900/40 px-2 py-0.5 rounded-full">
                        Recommended
                      </span>
                    )}
                    {quality === 'tester' && <SelectedMark quality="tester" />}
                  </div>
                  <div className="shrink-0 sm:text-right">
                    <p className={`text-2xl font-bold tracking-tight ${TIER_STYLES.tester.accent}`}>
                      {ALL_PACKAGES.tester.credits} credits
                    </p>
                    <p className="mt-1 text-sm font-semibold text-white">
                      Up to {ALL_PACKAGES.tester.maxImages} photos
                    </p>
                  </div>
                </div>
                <p className="mt-3 text-sm text-slate-300 leading-relaxed max-w-2xl">
                  {ALL_PACKAGES.tester.description}
                </p>
                <ul className="mt-3 space-y-1.5">
                  {detailFeatures(ALL_PACKAGES.tester.features).map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>
                {usageCredits == null && userCredits < ALL_PACKAGES.tester.credits && (
                  <div className="mt-4 pt-3 border-t border-emerald-900/40">
                    <p className="text-xs text-red-400 font-medium">Not enough credits</p>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        openBuyCredits('package_card');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.stopPropagation();
                          openBuyCredits('package_card');
                        }
                      }}
                      className="mt-1.5 inline-flex px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors"
                    >
                      Buy Credits
                    </span>
                  </div>
                )}
              </button>
            )}

            {/* Motion quality cards only when tiers actually differ. Otherwise
                show feature packages so we do not claim Standard/Premium/Ultra quality. */}
            {motionTiersLoaded && motionTiers.length > 1 && !identicalMotionTiers ? (
              <div
                className={`grid grid-cols-1 gap-4 mb-4 ${
                  motionTiers.length === 1 ? 'sm:grid-cols-1 max-w-md' : 'sm:grid-cols-3'
                }`}
              >
                {motionTiers.map((tier) => {
                  const isSelected =
                    quality !== 'tester' && selectedMotionTierId === tier.tier_id;
                  const styles =
                    MOTION_TIER_CARD_STYLES[tier.tier_key] ?? MOTION_TIER_CARD_STYLES.standard_motion;
                  const flatPrice = tier.credit_cost;
                  const badge = tier.badge_text || (tier.is_recommended ? 'Recommended' : null);

                  return (
                    <button
                      key={tier.tier_id}
                      type="button"
                      onClick={() => {
                        setSelectedMotionTierId(tier.tier_id);
                        setUserOverridePackage(true);
                        setQuality(qualityFromMotionTierKey(tier.tier_key));
                      }}
                      className={`relative flex flex-col h-full p-5 rounded-xl border-2 text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                        isSelected
                          ? `${styles.border} ${styles.bg} ring-1 ${styles.ring}`
                          : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                      }`}
                    >
                      {badge && (
                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1 px-3 py-1 bg-amber-500 text-black text-[11px] font-bold rounded-full whitespace-nowrap shadow-lg">
                          <Star className="w-3 h-3" />
                          {badge}
                        </span>
                      )}

                      <h3 className={`text-base font-bold mt-1 ${isSelected ? 'text-white' : 'text-slate-200'}`}>
                        {tier.display_name}
                      </h3>

                      <div className={`flex items-center gap-1.5 mt-2 text-sm font-bold ${styles.accent}`}>
                        <Coins className="w-4 h-4" />
                        {flatPrice} credits
                      </div>

                      <p className="text-xs text-slate-400 mt-2 leading-relaxed flex-1">
                        {tier.description}
                      </p>

                      <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-slate-400">
                        {tier.duration_seconds != null && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-900/60 border border-slate-700">
                            <Film className="w-3 h-3" />
                            {tier.duration_seconds}s / clip
                          </span>
                        )}
                        {tier.resolution && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-900/60 border border-slate-700">
                            <Monitor className="w-3 h-3" />
                            {tier.resolution}
                          </span>
                        )}
                        {tier.max_images != null && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-900/60 border border-slate-700">
                            <ImageIcon className="w-3 h-3" />
                            Up to {tier.max_images} images
                          </span>
                        )}
                      </div>

                      {userCredits < flatPrice && (
                        <div className="mt-3 pt-2 border-t border-slate-700/50">
                          <p className="text-xs text-red-400 font-medium">Not enough credits</p>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            ) : (
            /* Paid packages. Motion-tier cards above are only used when clip length actually differs. */
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-6 mb-4 mt-4 items-stretch">
              {PAID_PACKAGE_KEYS.map((key) => {
                const pkg = PACKAGES[key];
                const isSelected = quality === key;
                const styles = TIER_STYLES[key];
                const recommended = recommendedId === key;

                return (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => selectPackage(key)}
                    className={`relative flex flex-col h-full p-5 rounded-xl border-2 text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                      isSelected
                        ? `${styles.border} ${styles.bg} ring-1 ${styles.ring}`
                        : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                    }`}
                  >
                    {pkg.badge && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1 px-3 py-1 bg-amber-500 text-black text-[11px] font-bold rounded-full whitespace-nowrap shadow-lg">
                        <Star className="w-3 h-3" />
                        {pkg.badge}
                      </span>
                    )}
                    {recommended && !pkg.badge && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-blue-600 text-white text-[11px] font-bold rounded-full whitespace-nowrap">
                        Recommended
                      </span>
                    )}

                    <div className="flex items-start justify-between gap-2">
                      <h3 className={`text-lg font-bold leading-tight ${isSelected ? 'text-white' : 'text-slate-100'}`}>
                        {pkg.label}
                      </h3>
                      {isSelected && <SelectedMark quality={key} />}
                    </div>

                    {usageCredits != null ? (
                      <p className="mt-3 text-sm font-semibold text-slate-300">Credits calculated below</p>
                    ) : (
                      <p className={`mt-3 text-2xl font-bold tracking-tight ${styles.accent}`}>
                        {pkg.credits} credits
                      </p>
                    )}
                    <p className="mt-1 text-sm font-semibold text-white">
                      Up to {pkg.maxImages} photos
                    </p>

                    <p className="text-sm text-slate-300 mt-3 leading-relaxed">
                      {pkg.description}
                    </p>

                    <ul className="mt-4 flex-1 space-y-1.5">
                      {detailFeatures(pkg.features).map((feature) => (
                        <li key={feature} className="flex items-start gap-2 text-sm text-slate-300">
                          <Check className="w-3.5 h-3.5 text-green-400 shrink-0 mt-0.5" />
                          {feature}
                        </li>
                      ))}
                    </ul>

                    {usageCredits == null && userCredits < pkg.credits && (
                      <div className="mt-auto pt-3 border-t border-slate-700/50">
                        <p className="text-xs text-red-400 font-medium">Not enough credits</p>
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            openBuyCredits('package_card');
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.stopPropagation();
                              openBuyCredits('package_card');
                            }
                          }}
                          className="mt-1.5 inline-flex w-full justify-center px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors"
                        >
                          Buy Credits
                        </span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            )}

            {!showDistinctMotionTiers && (
              <p className="mb-4 text-sm text-slate-400 leading-relaxed">
                {IMAGEMOTION_CLIP_EXPLAINER}
              </p>
            )}

            <dl className="rounded-lg border border-slate-800 bg-slate-950/40 p-4 text-sm grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mb-4">
              <div>
                <dt className="text-slate-500">Photos selected</dt>
                <dd className="font-semibold text-white">{images.length}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Estimated finished duration</dt>
                <dd className="font-semibold text-white">
                  {formatFinishedDuration(localQuote.estimatedFinishedSeconds)}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">Total credits required</dt>
                <dd className="font-semibold text-white">{creditsNeeded}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Balance after generation</dt>
                <dd className={`font-semibold ${canAfford ? 'text-white' : 'text-red-400'}`}>
                  {userCredits - creditsNeeded}
                </dd>
              </div>
              {priceChanged && (
                <div className="sm:col-span-2 text-amber-300">
                  Price updated to {priceChanged.required} credits. Confirm to continue.
                </div>
              )}
            </dl>

            <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-4 text-sm space-y-1 mb-8">
              <p className="text-slate-300 flex items-center gap-1.5">
                <Coins className="w-3.5 h-3.5 text-blue-400" />
                Your balance: <span className="font-semibold text-white">{userCredits}</span> credits
              </p>
              <p className="text-slate-400">
                {chargeLabel} requires{' '}
                <span className="text-white font-medium">{creditsNeeded}</span> credits.
                {creditShortfall > 0 && (
                  <>
                    {' '}
                    You need <span className="text-amber-400 font-medium">{creditShortfall}</span> more
                    credits.
                  </>
                )}
              </p>
              {creditShortfall > 0 && (
                <button
                  type="button"
                  onClick={() => openBuyCredits('shortfall_banner')}
                  className="mt-2 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors"
                >
                  Buy credits
                </button>
              )}
            </div>

            <hr className="border-slate-800 my-8" />

            <div className="flex items-center gap-3 mb-6">
              <ImageIcon className="w-6 h-6 text-blue-500" />
              <h2 className="text-lg font-semibold text-white">Upload Photos</h2>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Project Name <span className="text-slate-500 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={projectName}
                onChange={(e) => {
                  setNameTouched(true);
                  setProjectName(e.target.value);
                }}
                placeholder="e.g. 42 Maple Avenue, London"
                maxLength={120}
                className="w-full px-4 py-2 border border-slate-700 rounded-lg bg-slate-800 text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="flex items-center gap-3 mb-2">
              <Zap className="w-5 h-5 text-blue-500" />
              <h3 className="text-sm font-semibold text-white">Upload Images</h3>
              <span className="text-xs text-slate-500">
                (max {maxImages} for {chargeLabel})
              </span>
            </div>
            <p className="text-sm text-slate-400 mb-4">
              Upload at least 2 images. Drag to reorder — this defines the video sequence.
              Keep every photo the same orientation so the video aspect can match your images.
            </p>
            <ImageUploader
              images={images}
              onChange={setImages}
              userId={userId}
              maxImages={maxImages}
            />

            {upgradeSuggestion && (
              <div className="mt-4 p-4 rounded-lg bg-amber-900/20 border border-amber-500/30 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-300">{upgradeSuggestion.message}</p>
                  {userCredits >= PACKAGES[upgradeSuggestion.targetTier].credits && (
                    <button
                      type="button"
                      onClick={() => selectPackage(upgradeSuggestion.targetTier)}
                      className="mt-2 px-3 py-1.5 text-xs font-medium text-white bg-amber-600 hover:bg-amber-500 rounded-lg transition-colors"
                    >
                      Switch to {PACKAGES[upgradeSuggestion.targetTier].label} (
                      {PACKAGES[upgradeSuggestion.targetTier].credits} credits)
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ───────── Step 2: Customise Video ───────── */}
        {step === 2 && (
          <div className="space-y-8">
            <div className="flex items-center gap-3 mb-2">
              <Settings className="w-6 h-6 text-blue-500" />
              <h2 className="text-lg font-semibold text-white">Customise Video</h2>
              <span
                className={`text-xs font-medium px-2 py-0.5 rounded-full ${TIER_STYLES[quality].bg} ${TIER_STYLES[quality].accent} border ${
                  quality === 'premium' || quality === 'ultra_premium'
                    ? 'border-amber-500/30'
                    : quality === 'tester'
                      ? 'border-emerald-500/30'
                      : 'border-blue-500/30'
                }`}
              >
                {selectedPackage.label}
              </span>
            </div>

            {isTester && (
              <div className="p-4 rounded-lg bg-emerald-900/20 border border-emerald-500/30 text-sm text-emerald-200">
                Tester Video uses fixed settings: dissolve transitions, a small ImageMotion watermark,
                and no music or custom branding. A paid package adds titles, music and your branding.
              </div>
            )}

            <SettingsSection icon={<SlidersHorizontal className="w-4 h-4 text-blue-400" />} title="Transition">
              <select
                value={settings.transition_effect || 'dissolve'}
                onChange={(e) => updateSetting('transition_effect', e.target.value)}
                disabled={dissolveOnly}
                className="w-full px-4 py-2 border border-slate-700 rounded-lg bg-slate-800 text-white focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
              >
                {transitions.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              {dissolveOnly && (
                <p className="text-[10px] text-slate-600 mt-1">
                  {isTester ? 'Tester videos use dissolve only.' : 'Quick Video uses dissolve transitions.'}{' '}
                  Upgrade to Standard or Premium for more styles.
                </p>
              )}
              {!dissolveOnly && !isPremiumPlus && (
                <p className="text-[10px] text-slate-600 mt-1">
                  Upgrade to Premium for 12+ transition styles
                </p>
              )}
            </SettingsSection>

            <SettingsSection icon={<Monitor className="w-4 h-4 text-blue-400" />} title="Output & Timing">
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-slate-500 mb-2">Aspect Ratio</label>
                  <div className="flex gap-2">
                    {ASPECT_RATIOS.map((ar) => (
                      <div
                        key={ar.value}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${
                          (settings.aspect_ratio || '16:9') === ar.value
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-slate-800 text-slate-500 border-slate-800'
                        }`}
                      >
                        <span className="flex items-center justify-center gap-2">
                          <span className="text-base">{ar.icon}</span>
                          {ar.label}
                        </span>
                        <span className="block text-[10px] text-slate-400 mt-0.5">{ar.description}</span>
                      </div>
                    ))}
                  </div>
                  {images.length > 0 && (
                    <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-900/20 p-2.5">
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
                      <p className="text-xs leading-relaxed text-amber-200/90">
                        Make sure your video aspect matches your images.
                        {detectedAspectRatio === '9:16'
                          ? ' These photos are portrait, so output is locked to 9:16. Use landscape photos for 16:9.'
                          : ' These photos are landscape, so output is locked to 16:9. Use portrait photos for 9:16.'}
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-xs text-slate-500 mb-2">Resolution</label>
                  <div className="flex gap-2">
                    {RESOLUTIONS.map((r) => (
                      <button
                        key={r.value}
                        type="button"
                        onClick={() => updateSetting('output_resolution', r.value as 'hd')}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                          settings.output_resolution === r.value
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-slate-800 text-slate-300 border-slate-700 hover:border-slate-600'
                        }`}
                      >
                        {r.label}
                        <span className="block text-[10px] text-slate-400 mt-0.5">
                          {(settings.aspect_ratio || '16:9') === '9:16' ? '1080 x 1920' : r.description}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </SettingsSection>

            <SettingsSection
              icon={<Palette className="w-4 h-4 text-blue-400" />}
              title="Visual Effects"
              locked={!isPremiumPlus}
              requiredTier="Premium"
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-slate-500 mb-2">Clip Filter</label>
                  <select
                    value={settings.filter || ''}
                    onChange={(e) => updateSetting('filter', e.target.value || null)}
                    className="w-full px-4 py-2 border border-slate-700 rounded-lg bg-slate-800 text-white focus:ring-2 focus:ring-blue-500"
                  >
                    {FILTERS.map((f) => (
                      <option key={f.value} value={f.value}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-2">Background Colour</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={settings.background_color || '#000000'}
                      onChange={(e) => updateSetting('background_color', e.target.value)}
                      className="w-10 h-10 rounded border border-slate-700 cursor-pointer bg-transparent"
                    />
                    <span className="text-sm text-slate-400 font-mono">
                      {settings.background_color || '#000000'}
                    </span>
                  </div>
                </div>
              </div>
            </SettingsSection>

            <SettingsSection
              icon={<Type className="w-4 h-4 text-blue-400" />}
              title="Text Overlays"
              locked={!isStandardPlus}
              requiredTier="Standard"
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Intro Title Card</label>
                  <input
                    type="text"
                    value={settings.intro_text || ''}
                    onChange={(e) => updateSetting('intro_text', e.target.value || null)}
                    placeholder="e.g. 123 Oak Street, London"
                    maxLength={80}
                    className="w-full px-4 py-2 border border-slate-700 rounded-lg bg-slate-800 text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {settings.intro_text && (
                    <TitleCardPreview
                      text={settings.intro_text}
                      fontSize={settings.title_font_size || 64}
                      backgroundColor={settings.background_color || '#000000'}
                      logoUrl={settings.logo_on_title_cards ? logoPreview || uploadedLogoUrl : null}
                      logoScale={settings.logo_scale || 0.15}
                      label="Intro"
                      aspectRatio={settings.aspect_ratio || '16:9'}
                    />
                  )}
                </div>

                <div>
                  <label className="block text-xs text-slate-500 mb-1">Outro Title Card</label>
                  <input
                    type="text"
                    value={settings.outro_text || ''}
                    onChange={(e) => updateSetting('outro_text', e.target.value || null)}
                    placeholder="e.g. Contact us: info@agency.com"
                    maxLength={80}
                    className="w-full px-4 py-2 border border-slate-700 rounded-lg bg-slate-800 text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {settings.outro_text && (
                    <TitleCardPreview
                      text={settings.outro_text}
                      fontSize={settings.title_font_size || 64}
                      backgroundColor={settings.background_color || '#000000'}
                      logoUrl={settings.logo_on_title_cards ? logoPreview || uploadedLogoUrl : null}
                      logoScale={settings.logo_scale || 0.15}
                      label="Outro"
                      aspectRatio={settings.aspect_ratio || '16:9'}
                    />
                  )}
                </div>

                <div>
                  <label className="block text-xs text-slate-500 mb-2">Text Size</label>
                  <div className="flex gap-2">
                    {TITLE_FONT_SIZES.map((s) => (
                      <button
                        key={s.value}
                        type="button"
                        onClick={() => updateSetting('title_font_size', s.value)}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                          (settings.title_font_size || 64) === s.value
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-slate-800 text-slate-300 border-slate-700 hover:border-slate-600'
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </SettingsSection>

            <hr className="border-slate-800" />

            <SettingsSection
              icon={<Film className="w-4 h-4 text-blue-400" />}
              title="Logo & Branding"
              locked={isTester}
              requiredTier={isTester ? 'Quick' : undefined}
            >
              {isTester ? (
                <p className="text-sm text-slate-500">
                  Tester videos include a small ImageMotion watermark. Custom branding unlocks on Quick
                  and above.
                </p>
              ) : (
                <>
                  {uploadedLogoUrl ? (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                      <img
                        src={logoPreview || uploadedLogoUrl}
                        alt="Logo preview"
                        className="h-12 w-12 object-contain rounded border border-slate-700 bg-slate-900 p-1"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white font-medium">Logo selected</p>
                        <p className="text-[10px] text-slate-500 truncate">
                          Ready to use as watermark{isStandardPlus ? ' or bumper card' : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="cursor-pointer px-3 py-1.5 text-xs text-blue-400 bg-blue-900/30 rounded-lg hover:bg-blue-900/50 transition-colors">
                          Change
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/svg+xml,image/webp"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleLogoUpload(file);
                            }}
                            className="hidden"
                          />
                        </label>
                        <button
                          type="button"
                          onClick={() => {
                            setLogoPreview(null);
                            setUploadedLogoUrl(null);
                            updateSetting('logo_intro_card', false);
                            updateSetting('logo_outro_card', false);
                            updateSetting('logo_on_title_cards', false);
                          }}
                          className="p-1.5 text-slate-500 hover:text-red-400 transition-colors rounded-lg hover:bg-red-900/20"
                          title="Remove logo"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <label className="inline-flex items-center gap-2 px-4 py-2.5 text-sm text-blue-400 bg-blue-900/30 rounded-lg hover:bg-blue-900/50 transition-colors cursor-pointer font-medium">
                          Upload New Logo
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/svg+xml,image/webp"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleLogoUpload(file);
                            }}
                            className="hidden"
                          />
                        </label>
                        {brandLogoUrl && (
                          <button
                            type="button"
                            onClick={() => {
                              setUploadedLogoUrl(brandLogoUrl);
                              setLogoPreview(brandLogoUrl);
                            }}
                            className="inline-flex items-center gap-2 px-4 py-2.5 text-sm text-purple-400 bg-purple-900/30 rounded-lg hover:bg-purple-900/50 transition-colors font-medium"
                          >
                            Use Brand Logo
                          </button>
                        )}
                      </div>
                      <p className="text-[10px] text-slate-600">
                        PNG, JPG, SVG, or WebP — transparent backgrounds work best. Or leave blank for no
                        logo.
                      </p>
                    </div>
                  )}

                  {savedLogos.length > 0 && !uploadedLogoUrl && (
                    <div className="mt-3">
                      <p className="text-xs text-slate-400 font-medium mb-2">Your Saved Logos</p>
                      <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                        {savedLogos.map((logo) => (
                          <div key={logo.id} className="relative group">
                            <button
                              type="button"
                              onClick={() => handleSelectSavedLogo(logo)}
                              className="w-full aspect-square rounded-lg border border-slate-700 bg-slate-800/50 hover:border-blue-500 transition-colors p-2 flex items-center justify-center"
                            >
                              <img
                                src={logo.logo_url}
                                alt={logo.name || 'Saved logo'}
                                className="max-w-full max-h-full object-contain"
                              />
                            </button>
                            {logo.is_default && (
                              <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                                <Check className="w-2.5 h-2.5 text-white" />
                              </span>
                            )}
                            <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-0.5">
                              {!logo.is_default && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSetDefaultLogo(logo.id);
                                  }}
                                  className="p-1 bg-black/60 rounded text-slate-400 hover:text-blue-400 transition-colors"
                                  title="Set as default"
                                >
                                  <Star className="w-3 h-3" />
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteSavedLogo(logo.id);
                                }}
                                className="p-1 bg-black/60 rounded text-slate-400 hover:text-red-400 transition-colors"
                                title="Delete"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {logoUploading && (
                    <p className="text-xs text-blue-400 mt-2 flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" /> Uploading...
                    </p>
                  )}

                  {uploadedLogoUrl && (
                    <div className="mt-5 space-y-5">
                      <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                        Logo Options
                      </span>

                      <div className="p-4 rounded-lg bg-slate-800/30 border border-slate-700/50 space-y-4">
                        <span className="text-xs font-medium text-slate-300 block">Watermark on Clips</span>
                        <div>
                          <label className="block text-xs text-slate-500 mb-2">Position</label>
                          <div className="grid grid-cols-3 gap-1 w-48">
                            {LOGO_POSITIONS.map((pos) => (
                              <button
                                key={pos.value}
                                type="button"
                                onClick={() => updateSetting('logo_position', pos.value)}
                                className={`px-2 py-1.5 text-[10px] rounded border transition-colors ${
                                  settings.logo_position === pos.value
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-600'
                                }`}
                              >
                                {pos.label}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">
                            Size: {Math.round((settings.logo_scale || 0.15) * 100)}%
                          </label>
                          <input
                            type="range"
                            min={0.05}
                            max={0.4}
                            step={0.01}
                            value={settings.logo_scale || 0.15}
                            onChange={(e) => updateSetting('logo_scale', Number(e.target.value))}
                            className="w-full accent-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">
                            Opacity: {Math.round((settings.logo_opacity || 0.8) * 100)}%
                          </label>
                          <input
                            type="range"
                            min={0.2}
                            max={1}
                            step={0.05}
                            value={settings.logo_opacity || 0.8}
                            onChange={(e) => updateSetting('logo_opacity', Number(e.target.value))}
                            className="w-full accent-blue-500"
                          />
                        </div>
                        <div>
                          <span className="text-[10px] text-slate-600 uppercase tracking-wider">
                            Watermark Preview
                          </span>
                          <div
                            className={`relative mt-1 rounded border border-slate-700 overflow-hidden bg-slate-800 ${
                              (settings.aspect_ratio || '16:9') === '9:16' ? 'w-1/2 mx-auto' : 'w-full'
                            }`}
                            style={{
                              aspectRatio:
                                (settings.aspect_ratio || '16:9') === '9:16' ? '9 / 16' : '16 / 9',
                            }}
                          >
                            <div className="absolute inset-0 flex items-center justify-center opacity-10 text-slate-500 text-xs">
                              Video frame
                            </div>
                            <img
                              src={logoPreview || uploadedLogoUrl}
                              alt="Watermark preview"
                              className="absolute object-contain"
                              style={{
                                width: `${Math.round((settings.logo_scale || 0.15) * 100)}%`,
                                opacity: settings.logo_opacity || 0.8,
                                ...getLogoPositionStyle(settings.logo_position),
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      <div className={!isStandardPlus ? 'relative opacity-40 pointer-events-none select-none' : ''}>
                        <div className="p-4 rounded-lg bg-slate-800/30 border border-slate-700/50 space-y-4">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium text-slate-300">Logo Bumper Cards</span>
                              {!isStandardPlus && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
                                  <Lock className="w-3 h-3" />
                                  Standard
                                </span>
                              )}
                            </div>
                            <p className="text-[10px] text-slate-600">
                              Full-screen branded cards at the start and/or end of your video
                            </p>
                          </div>

                          <div className="space-y-2">
                            <label className="flex items-center gap-3 cursor-pointer group">
                              <div
                                className={`relative w-10 h-5 rounded-full transition-colors ${
                                  settings.logo_intro_card ? 'bg-blue-600' : 'bg-slate-700'
                                }`}
                                onClick={() =>
                                  isStandardPlus &&
                                  updateSetting('logo_intro_card', !settings.logo_intro_card)
                                }
                              >
                                <div
                                  className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                                    settings.logo_intro_card ? 'translate-x-5' : ''
                                  }`}
                                />
                              </div>
                              <span className="text-xs text-slate-400 group-hover:text-slate-300 transition-colors">
                                Logo intro card (start of video)
                              </span>
                            </label>
                            <label className="flex items-center gap-3 cursor-pointer group">
                              <div
                                className={`relative w-10 h-5 rounded-full transition-colors ${
                                  settings.logo_outro_card ? 'bg-blue-600' : 'bg-slate-700'
                                }`}
                                onClick={() =>
                                  isStandardPlus &&
                                  updateSetting('logo_outro_card', !settings.logo_outro_card)
                                }
                              >
                                <div
                                  className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                                    settings.logo_outro_card ? 'translate-x-5' : ''
                                  }`}
                                />
                              </div>
                              <span className="text-xs text-slate-400 group-hover:text-slate-300 transition-colors">
                                Logo outro card (end of video)
                              </span>
                            </label>
                          </div>

                          {(settings.logo_intro_card || settings.logo_outro_card) && (
                            <div className="space-y-4 pt-3 border-t border-slate-700/50">
                              <div>
                                <label className="block text-xs text-slate-500 mb-2">Card Duration</label>
                                <div className="flex gap-2">
                                  {LOGO_BUMPER_DURATIONS.map((d) => (
                                    <button
                                      key={d.value}
                                      type="button"
                                      onClick={() => updateSetting('logo_bumper_duration', d.value)}
                                      className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                                        (settings.logo_bumper_duration || 3) === d.value
                                          ? 'bg-blue-600 text-white border-blue-600'
                                          : 'bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-600'
                                      }`}
                                    >
                                      {d.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs text-slate-500 mb-2">Animation Style</label>
                                <div className="flex gap-2 flex-wrap">
                                  {LOGO_BUMPER_ANIMATIONS.map((a) => (
                                    <button
                                      key={a.value}
                                      type="button"
                                      onClick={() => updateSetting('logo_bumper_animation', a.value)}
                                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                                        (settings.logo_bumper_animation || 'fadeIn') === a.value
                                          ? 'bg-blue-600 text-white border-blue-600'
                                          : 'bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-600'
                                      }`}
                                    >
                                      {a.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs text-slate-500 mb-1">Tagline (optional)</label>
                                <input
                                  type="text"
                                  value={settings.logo_bumper_tagline || ''}
                                  onChange={(e) =>
                                    updateSetting('logo_bumper_tagline', e.target.value || null)
                                  }
                                  placeholder="e.g. Your Agency Name • yoursite.com"
                                  maxLength={60}
                                  className="w-full px-3 py-2 text-sm border border-slate-700 rounded-lg bg-slate-800 text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                              </div>
                              {settings.logo_intro_card && (
                                <TitleCardPreview
                                  logoUrl={logoPreview || uploadedLogoUrl}
                                  logoScale={settings.logo_scale || 0.15}
                                  backgroundColor={settings.background_color || '#000000'}
                                  tagline={settings.logo_bumper_tagline}
                                  animation={settings.logo_bumper_animation || 'fadeIn'}
                                  label="Logo Intro"
                                  aspectRatio={settings.aspect_ratio || '16:9'}
                                />
                              )}
                              {settings.logo_outro_card && (
                                <TitleCardPreview
                                  logoUrl={logoPreview || uploadedLogoUrl}
                                  logoScale={settings.logo_scale || 0.15}
                                  backgroundColor={settings.background_color || '#000000'}
                                  tagline={settings.logo_bumper_tagline}
                                  animation={settings.logo_bumper_animation || 'fadeIn'}
                                  label="Logo Outro"
                                  aspectRatio={settings.aspect_ratio || '16:9'}
                                />
                              )}
                            </div>
                          )}
                        </div>
                        {!isStandardPlus && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-xs text-amber-400 bg-black/70 px-3 py-1 rounded-full border border-amber-500/30">
                              Select Standard or above to unlock
                            </span>
                          </div>
                        )}
                      </div>

                      <div className={!isStandardPlus ? 'opacity-40 pointer-events-none select-none' : ''}>
                        <label className="flex items-center gap-3 cursor-pointer group">
                          <div
                            className={`relative w-10 h-5 rounded-full transition-colors ${
                              settings.logo_on_title_cards ? 'bg-blue-600' : 'bg-slate-700'
                            }`}
                            onClick={() =>
                              isStandardPlus &&
                              updateSetting('logo_on_title_cards', !settings.logo_on_title_cards)
                            }
                          >
                            <div
                              className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                                settings.logo_on_title_cards ? 'translate-x-5' : ''
                              }`}
                            />
                          </div>
                          <span className="text-xs text-slate-400 group-hover:text-slate-300 transition-colors">
                            Show logo on intro &amp; outro text cards
                          </span>
                          {!isStandardPlus && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
                              <Lock className="w-3 h-3" />
                              Standard
                            </span>
                          )}
                        </label>
                      </div>
                    </div>
                  )}
                </>
              )}
            </SettingsSection>

            <SettingsSection
              icon={<Music className="w-4 h-4 text-blue-400" />}
              title="Background Music"
              locked={!isStandardPlus}
              requiredTier="Standard"
            >
              {isStandardPlus ? (
                <div className="space-y-3">
                  <div className="flex gap-3">
                    {(['library', 'upload', 'url', 'none'] as const).map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => setMusicMode(mode)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                          musicMode === mode
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-slate-800 text-slate-300 border-slate-700 hover:border-slate-600'
                        }`}
                      >
                        {mode === 'none' ? 'No Music' : mode === 'upload' ? 'Upload File' : mode === 'url' ? 'Paste URL' : 'Library'}
                      </button>
                    ))}
                  </div>

                  {musicMode === 'library' && (
                    <div className="space-y-2">
                      <LibraryTrackList
                        tracks={libraryTracks}
                        onAdd={(track) => void handleLibraryTrack(track)}
                        addingId={libraryAddingId}
                        addLabel="Use"
                        supabase={supabase}
                      />
                      {libraryTitle && uploadedMusicUrl && !musicUploading && (
                        <p className="text-xs text-green-400">{libraryTitle} added</p>
                      )}
                    </div>
                  )}

                  {musicMode === 'upload' && (
                    <div>
                      <input
                        type="file"
                        accept="audio/mpeg,audio/wav,audio/mp4,audio/x-m4a"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleMusicFileUpload(file);
                        }}
                        className="block w-full text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-900/30 file:text-blue-400 hover:file:bg-blue-900/50"
                      />
                      {musicUploading && (
                        <p className="text-xs text-blue-400 mt-1 flex items-center gap-1">
                          <Loader2 className="w-3 h-3 animate-spin" /> Uploading...
                        </p>
                      )}
                      {uploadedMusicUrl && !musicUploading && (
                        <p className="text-xs text-green-400 mt-1">Music uploaded</p>
                      )}
                    </div>
                  )}

                  {musicMode === 'url' && (
                    <input
                      type="url"
                      value={musicUrl}
                      onChange={(e) => setMusicUrl(e.target.value)}
                      placeholder="https://example.com/music.mp3"
                      className="w-full px-4 py-2 border border-slate-700 rounded-lg bg-slate-800 text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  )}

                  {musicMode !== 'none' && (
                    <div className="space-y-3">
                      <span className="text-xs text-slate-500">Music Options</span>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">
                          Volume: {Math.round((settings.music_volume ?? 0.7) * 100)}%
                        </label>
                        <input
                          type="range"
                          min={0}
                          max={1}
                          step={0.05}
                          value={settings.music_volume ?? 0.7}
                          onChange={(e) => updateSetting('music_volume', Number(e.target.value))}
                          className="w-full accent-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Audio Effect</label>
                        <select
                          value={settings.music_effect || 'fadeInFadeOut'}
                          onChange={(e) => updateSetting('music_effect', e.target.value)}
                          className="w-full px-4 py-2 border border-slate-700 rounded-lg bg-slate-800 text-white focus:ring-2 focus:ring-blue-500"
                        >
                          {MUSIC_EFFECTS.map((ef) => (
                            <option key={ef.value} value={ef.value}>
                              {ef.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-500">
                  Upload a music track or paste a URL to add background audio to your video.
                </p>
              )}
            </SettingsSection>
          </div>
        )}

        {/* ───────── Step 3: Review & Create ───────── */}
        {step === 3 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-white">Review & Create</h2>

            <div>
              <h3 className="text-sm font-medium text-slate-400 mb-2">Images ({images.length})</h3>
              <div className="grid grid-cols-5 sm:grid-cols-8 gap-2">
                {images.map((img, index) => (
                  <div key={img.id} className="relative aspect-square">
                    <img
                      src={img.preview}
                      alt={`Image ${index + 1}`}
                      className="w-full h-full object-cover rounded"
                    />
                    <span className="absolute -top-1 -left-1 w-5 h-5 bg-blue-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {index + 1}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {projectName.trim() && (
              <div>
                <span className="text-slate-500 text-sm">Project Name</span>
                <p className="font-medium text-white">{projectName.trim()}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-slate-500">Package</span>
                <p className={`font-medium ${TIER_STYLES[quality].accent}`}>
                  {chargeLabel}
                  {(quality === 'premium' || selectedMotionTier?.is_recommended) && (
                    <Star className="w-3 h-3 inline ml-1" />
                  )}
                  {isTester && <Gift className="w-3 h-3 inline ml-1" />}
                </p>
              </div>
              <div>
                <span className="text-slate-500">Cost</span>
                <p className="font-medium text-white flex items-center gap-1">
                  <Coins className="w-3 h-3 text-blue-400" />
                  {creditsNeeded} credits
                </p>
              </div>
              <div>
                <span className="text-slate-500">Transition</span>
                <p className="font-medium text-white">
                  {(isPremiumPlus ? PREMIUM_TRANSITIONS : STANDARD_TRANSITIONS).find(
                    (t) => t.value === (settings.transition_effect || 'dissolve')
                  )?.label || settings.transition_effect}
                </p>
              </div>
              <div>
                <span className="text-slate-500">Aspect Ratio</span>
                <p className="font-medium text-white">
                  {ASPECT_RATIOS.find((ar) => ar.value === (settings.aspect_ratio || '16:9'))?.label ||
                    'Landscape'}{' '}
                  ({settings.aspect_ratio || '16:9'})
                  <span className="block text-[10px] font-normal text-slate-500">Auto-detected from photos</span>
                </p>
              </div>
              <div className="col-span-2 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-900/20 p-3">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
                <p className="text-xs leading-relaxed text-amber-200/90">
                  Make sure your video is set to the aspect of your images.
                  {(settings.aspect_ratio || '16:9') === '9:16'
                    ? ' Portrait photos → 9:16 video. Landscape photos will look cropped or letterboxed.'
                    : ' Landscape photos → 16:9 video. Portrait photos will look cropped or letterboxed.'}
                </p>
              </div>
              <div>
                <span className="text-slate-500">Resolution</span>
                <p className="font-medium text-white">
                  {RESOLUTIONS.find((r) => r.value === settings.output_resolution)?.label || 'Composed 1080p'}
                </p>
              </div>
              {isPremiumPlus && settings.filter && (
                <div>
                  <span className="text-slate-500">Filter</span>
                  <p className="font-medium text-white capitalize">{settings.filter}</p>
                </div>
              )}
              {isStandardPlus && settings.intro_text && (
                <div>
                  <span className="text-slate-500">Intro Card</span>
                  <p className="font-medium text-white truncate">{settings.intro_text}</p>
                </div>
              )}
              {isStandardPlus && settings.outro_text && (
                <div>
                  <span className="text-slate-500">Outro Card</span>
                  <p className="font-medium text-white truncate">{settings.outro_text}</p>
                </div>
              )}
              <div>
                <span className="text-slate-500">Music</span>
                <p className="font-medium text-white">
                  {isTester || musicMode === 'none'
                    ? 'None'
                    : musicMode === 'library'
                      ? libraryTitle || 'Library track'
                      : musicMode === 'upload'
                        ? 'Uploaded file'
                        : 'URL provided'}
                </p>
              </div>
              <div>
                <span className="text-slate-500">Logo</span>
                <p className="font-medium text-white">
                  {isTester ? 'ImageMotion watermark' : uploadedLogoUrl ? 'Uploaded' : 'None'}
                  {!isTester &&
                    uploadedLogoUrl &&
                    isStandardPlus &&
                    (settings.logo_intro_card || settings.logo_outro_card) && (
                      <span className="block text-[10px] text-slate-500 mt-0.5">
                        Bumper:{' '}
                        {[settings.logo_intro_card && 'intro', settings.logo_outro_card && 'outro']
                          .filter(Boolean)
                          .join(' + ')}{' '}
                        ({settings.logo_bumper_duration || 3}s each)
                      </span>
                    )}
                </p>
              </div>
              <div>
                <span className="text-slate-500">Est. Duration</span>
                <p className="font-medium text-white">{formatFinishedDuration(localQuote.estimatedFinishedSeconds)}</p>
              </div>
            </div>

            {creditShortfall > 0 && (
              <div className="p-4 rounded-lg bg-amber-900/20 border border-amber-500/30 text-sm text-amber-200">
                Your balance: {userCredits} credits. {chargeLabel} requires {creditsNeeded}{' '}
                credits. You need {creditShortfall} more credits.
                <button
                  type="button"
                  onClick={() => openBuyCredits('review_shortfall')}
                  className="mt-2 block px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors"
                >
                  Buy credits
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sticky bottom bar */}
      <div className="fixed bottom-0 inset-x-0 z-40 border-t border-slate-800 bg-slate-950/95 backdrop-blur-md">
        {step === 3 && (
          <div className="max-w-[1280px] mx-auto px-4 sm:px-6 pt-3 flex items-center justify-end">
            <p className="text-sm text-slate-300 flex items-center gap-1.5" aria-live="polite">
              <Coins className="w-4 h-4 text-blue-400" />
              This generation will use{' '}
              <span className="font-semibold text-white">{creditsNeeded} credits</span>
            </p>
          </div>
        )}
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => (step === 1 ? onNavigate('DASHBOARD') : setStep(step - 1))}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-300 bg-slate-900 border border-slate-700 rounded-lg hover:bg-slate-800 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {step === 1 ? 'Cancel' : 'Back'}
          </button>

          <div className="hidden sm:block text-xs text-slate-500 text-center">
            {chargeLabel} · {creditsNeeded} credits · balance {userCredits}
          </div>

          <button
            type="button"
            onClick={handleStickyPrimary}
            disabled={stickyDisabled}
            className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
              !canAfford
                ? 'bg-amber-600 hover:bg-amber-500'
                : isTester && step === 3
                  ? 'bg-emerald-600 hover:bg-emerald-500'
                  : step === 3
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Submitting...
              </>
            ) : (
              <>
                {step < 3 && canAfford ? <ArrowRight className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                {stickyCtaLabel}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
